import { getItemImage } from '@/shared/utils/itemImages';
import { getFishImage } from '@/shared/utils/fishesImages';

/**
 * A tradução entre o backend V2 e o que as stores do cliente já esperam.
 *
 * Existe para que a migração não seja um "big bang": as mutations (`SET_PLAYER_STATE`,
 * `SET_RODS`, ...) continuam recebendo a forma que sempre receberam, e só este
 * arquivo sabe que o servidor mudou. Quando as stores forem reescritas para o
 * vocabulário do V2, é este arquivo que encolhe até sumir.
 *
 * O V2 fala português (`gold`, `staminaAtual`, `nivel`) e o cliente fala inglês
 * (`gold`, `currentStamina`, `level`). A tradução mora aqui, num lugar só.
 */

/**
 * O perfil do jogador.
 *
 * `playerId` vem de fora porque o `id` do perfil do V2 é o UUID público, e não
 * serve para endereçar rota. Quem endereça é `players.id`, que o cliente
 * descobre em `GET /api/auth/me`.
 *
 * Campos que o V2 ainda não tem saem com o valor neutro que a mutation já usa
 * como padrão — `taxes`, `dailyReward`, `clan`. Não são zeros inventados: são o
 * que o cliente assume quando o servidor não fala do assunto.
 */
export function perfilParaPlayerState(perfil, playerId) {
  const equipamento = perfil.equipamento || {};
  return {
    id: playerId,
    identifier: perfil.username || '',
    level: perfil.nivel,
    experience: perfil.experiencia,
    maxExperience: perfil.maxExperiencia,
    gold: perfil.gold,
    paralleliums: perfil.paralleliums,
    fishCaught: perfil.peixesCapturados,
    totalFishCaught: perfil.peixesCapturados,
    currentStamina: perfil.staminaAtual,
    maxStamina: perfil.staminaMaxima,
    /**
     * Segundos até o próximo ponto de energia cair, direto do servidor.
     *
     * A regeneração é preguiçosa lá: ninguém empurra o número novo para cá, e
     * antes de 04/09/2026 nada nesta tela relia o perfil — a barra ficava
     * parada com o jogo aberto a tarde inteira. É este campo que diz ao
     * relógio da energia (`player/iniciarRelogioDaEnergia`) quando reler.
     */
    nextRecharge: perfil.proximaRecargaEm ?? 0,
    // Olhar de Águia (1.3): o mítico que um evento chama no mapa atual; nulo sem a habilidade.
    cardumeLendario: perfil.cardumeLendario ?? null,
    // O V2 devolve `{ id, nome }`; a mutation lê `.name` e cai para o valor cru.
    currentMap: perfil.mapaAtual ? { id: perfil.mapaAtual.id, name: perfil.mapaAtual.nome } : null,

    fishingPower: perfil.atributos?.fishingPower ?? 0,
    catchRate: perfil.atributos?.catchRate ?? 0.5,
    criticalRate: perfil.atributos?.criticalRate ?? 0.05,
    reelSpeed: perfil.atributos?.reelSpeed ?? 1,

    equipment: {
      rod: equipamento.vara
        ? {
            id: equipamento.vara.id,
            name: equipamento.vara.nome,
            // `{ current, maxDurability }` é a forma que as telas sempre leram
            // (`FishingAction`, `ItemCard`); número solto quebrava a barra.
            durability: {
              current: equipamento.vara.durabilidade ?? 0,
              maxDurability: equipamento.vara.durabilidadeMaxima ?? 0,
            },
            image: getItemImage(equipamento.vara.nome),
          }
        : null,
      boat: equipamento.barco
        ? {
            id: equipamento.barco.id,
            name: equipamento.barco.nome,
            // O nível que libera mapa (`maps.boatLevel`). `Maps.vue` compara
            // `equipment.boat.level` com o requisito do mapa.
            level: equipamento.barco.nivel,
            image: getItemImage(equipamento.barco.nome),
          }
        : null,
      house: equipamento.casa
        ? {
            id: equipamento.casa.id,
            name: equipamento.casa.nome,
            image: getItemImage(equipamento.casa.nome),
          }
        : null,
    },

    /**
     * O inventário vem de outra rota (`GET /player/:id/inventory`), e por isso
     * entra vazio aqui. Quem preenche é `carregarJogador`, que faz as duas
     * chamadas e junta — deixar `undefined` faria a mutation estourar em
     * `playerData.inventory.fish`.
     */
    inventory: { fish: [], rods: [], baits: [], boats: [], houses: [], licenses: [] },

    purchasedMaps: [],

    // Desde a segunda leva, o perfil do V2 fala destes assuntos — os valores
    // deixaram de ser o chute neutro do começo da migração.
    tickets: perfil.tickets ?? 0,
    clanCoins: perfil.clanCoins ?? 0,
    taxes: perfil.impostosPendentes ?? false,
    dailyReward: perfil.recompensaDiariaResgatada ?? false,
    firstFishOfDay: perfil.primeiroPeixeDoDia ?? false,
    isRefrigerator: perfil.temGeladeira ?? false,
    isResting: perfil.descansando ?? false,
    clan: perfil.cla ? { id: perfil.cla.id, name: perfil.cla.nome } : null,
    clan_invites: [],
    waiting_clans: [],
    claimedDailyMissionReward: perfil.recompensaDeMissoesResgatada ?? false,
  };
}

/** Um peixe do inventário. */
export function peixeDoInventario(item) {
  return {
    id: item.id,
    fishingId: item.fishingId,
    name: item.nome,
    rarity: item.raridade,
    // O peso vem como string decimal: o backend não arredonda porque o peso
    // entra no preço de venda.
    weight: Number(item.peso),
    value: item.valor,
    caughtAt: item.capturadoEm,
    // `getFishImage`, não `getItemImage`: peixe mora em `fishesImages`. Com o util de itens a
    // URL quebrava e toda a coleção caía na imagem-reserva — quatro peixes idênticos na tela.
    image: getFishImage(item.nome),
  };
}

/**
 * O resultado da pescaria.
 *
 * O V2 devolve uma união discriminada por `resultado`; o cliente espera
 * `{ success, fish }` com um `status` quando dá errado. Os três motivos de
 * fracasso são distintos de propósito e a tradução os preserva: "sem energia"
 * pede descanso, "sem peixes" é mapa sem peixe ligado no catálogo — o caso de
 * `Costa Dourada` e `Ilhas Cazu` — e "escapou" é só azar.
 */
export function resultadoDaPescaria(dados) {
  if (dados.resultado === 'capturou') {
    return {
      success: true,
      status: 'CAUGHT',
      critical: dados.critico,
      experienceGained: dados.experienciaGanha,
      /** O bônus do primeiro peixe do dia, já dentro de `experienceGained`. Zero nos demais. */
      firstFishBonus: dados.bonusPrimeiroPeixe ?? 0,
      leveledUp: dados.subiuDeNivel,
      playerLevel: dados.nivel,
      /** A barra de XP depois do lance — a tela aplica sem esperar o perfil. */
      playerExperience: dados.experiencia,
      maxExperience: dados.maxExperiencia,
      staminaRemaining: dados.staminaRestante,
      staminaCost: dados.custoDeStamina,
      /**
       * Atenção ao `id`: aqui ele é o **id da espécie** (`fishings.id`), porque
       * é isso que o servidor devolve ao pescar e é o que a missão diária de
       * "peixe específico" compara. No inventário, `id` é outra coisa — o id da
       * **linha** de `player_fish`, que é o que a venda aceita.
       *
       * Os dois convivem porque cada tela precisa de um. `fishingId` está
       * repetido nos dois lados para quem quiser a espécie sem depender de qual
       * objeto está na mão.
       */
      fish: {
        id: dados.peixe.id,
        fishingId: dados.peixe.id,
        name: dados.peixe.nome,
        rarity: dados.peixe.raridade,
        weight: Number(dados.peixe.peso),
        value: dados.peixe.valor,
        image: getFishImage(dados.peixe.nome),
        /**
         * O crítico TAMBÉM no peixe: `FishingAction` entrega `result.fish` ao
         * `CatchResult`, que lê `fish.critical`. Só na raiz, a faixa "Fisgada
         * crítica!" nunca aparecia e o crítico parecia não fazer nada
         * (28/09/2026). O crítico é peso (× 1,5), não raridade.
         */
        critical: Boolean(dados.critico),
      },
    };
  }

  /**
   * O lance perdido tem três motivos no servidor, como tinha no Strapi: a linha
   * quebrou (e custou o dobro da energia), o peixe fugiu, ou nada mordeu. Cada
   * um tem a sua frase; o `status` genérico continua `ESCAPED` para as telas
   * que só distinguem sucesso de fracasso.
   */
  const motivosDaFalha = {
    linhaQuebrada: { status: 'LINE_BREAK', message: 'fishing.result.lineBreak' },
    escapou: { status: 'ESCAPED', message: 'fishing.result.escaped' },
    nadaMordeu: { status: 'NO_BITE', message: 'fishing.result.noBite' },
  };
  const motivos = {
    escapou: motivosDaFalha[dados.motivo] || motivosDaFalha.escapou,
    semEnergia: { status: 'NO_STAMINA', message: 'fishing.result.noStamina' },
    semPeixes: { status: 'NO_FISH', message: 'fishing.result.noFish' },
  };
  const motivo = motivos[dados.resultado] || { status: 'ERROR', message: 'fishing.result.error' };

  return {
    success: false,
    status: motivo.status,
    message: motivo.message,
    staminaRemaining: dados.staminaRestante,
    staminaCost: dados.custoDeStamina ?? 0,
    fish: null,
  };
}

/**
 * Uma peça do baú (`GET /player/:id/gear`): vara, barco ou casa.
 *
 * Atenção aos dois ids: `id` é o da **instância** (`player_gear.id`) — é o que
 * equipar aceita, porque a mesma vara comprada duas vezes são duas linhas com
 * durabilidades próprias. `baseId` é o id do catálogo, para comparar espécie
 * ("é a Vara de Junco?") sem depender de qual instância está na mão. Trocar um
 * pelo outro equipa a peça errada — ou nenhuma.
 *
 * `durability` é objeto `{ current, maxDurability }` porque é assim que
 * `FishingAction.vue` e `ItemCard.vue` sempre leram (`rod.durability.current`).
 */
export function pecaDoEquipamento(peca) {
  const slots = { rod: 'rods', boat: 'boats', house: 'houses' };
  return {
    id: peca.gearId,
    baseId: peca.itemId,
    category: slots[peca.slot],
    name: peca.nome,
    rarity: peca.raridade,
    equipped: peca.equipado,
    durability:
      peca.slot === 'rod'
        ? { current: peca.durabilidade ?? 0, maxDurability: peca.durabilidadeMaxima ?? 0 }
        : null,
    power: peca.poder ?? undefined,
    speed: peca.velocidade ?? undefined,
    level: peca.nivelDeBarco ?? undefined,
    rechargeStamina: peca.recargaDeEnergia ? Number(peca.recargaDeEnergia) : undefined,
    image: getItemImage(peca.nome),
  };
}

/**
 * Uma posição do ranking de jogadores. Os três rankings compartilham a forma;
 * `RankingPlayers.vue` lê `position`, `username` e o número da lista dele.
 */
export function posicaoDeJogador(posicao) {
  return {
    position: posicao.posicao,
    username: posicao.jogador,
    level: posicao.nivel,
    experience: posicao.experiencia,
    gold: posicao.gold,
    totalFishCaught: posicao.totalDePeixes,
  };
}

/** Uma posição do ranking de clãs. `pontuation` é o nome que a tela já usa. */
export function posicaoDeCla(posicao) {
  return {
    position: posicao.posicao,
    id: posicao.clanId,
    name: posicao.nome,
    pontuation: posicao.pontuacao,
    leaderName: posicao.lider,
    level: posicao.nivel ?? 1,
    flag: posicao.bandeira ?? null,
    // O país (11/09/2026) é outra coisa que a bandeira: um código da folha, não uma URL.
    country: posicao.pais ?? null,
    members: posicao.membros,
  };
}

/**
 * O catálogo.
 *
 * As mutations da loja (`SET_RODS`, `SET_BAITS`, ...) leem `response.data` e
 * campos em inglês, então é essa a forma devolvida — inclusive o `image`, que o
 * cliente resolve pelo nome do item e o backend de propósito não manda.
 */
export function itemDoCatalogo(item) {
  return {
    id: item.id,
    name: item.nome,
    description: item.descricao || '',
    price: item.preco,
    requiredLevel: item.nivelExigido,
    rarity: item.raridade,
    stackable: item.empilhavel,
    power: item.poder,
    durability: item.durabilidade,
    speed: item.velocidade,
    level: item.nivelDeBarco,
    boatLevel: item.nivelDeBarco,
    rechargeStamina: item.recargaDeEnergia ? Number(item.recargaDeEnergia) : undefined,
    fishingPower: item.poderDePesca,
    type: item.categoria,
    image: getItemImage(item.nome),
  };
}

/* ------------------------------------------------------------------------ */
/* Segunda leva da migração: missões, premium, competições, clã, negócio,   */
/* eventos, mercado, roleta e perfil público.                               */
/* ------------------------------------------------------------------------ */

/**
 * Uma missão do dia. O V2 devolve progresso absoluto (`progresso`/`objetivo`);
 * a barra do modal usa porcentagem — calculada aqui, num lugar só.
 */
export function missaoDoDia(missao) {
  const progress =
    missao.objetivo > 0 ? Math.min(100, Math.round((missao.progresso / missao.objetivo) * 100)) : 0;
  return {
    id: missao.id,
    missionId: missao.missaoId,
    title: missao.titulo,
    type: missao.tipo,
    amountRequired: missao.objetivo,
    currentAmount: missao.progresso,
    isComplete: missao.completa,
    claimed: missao.resgatada,
    progress,
  };
}

/** Um item premium do catálogo, na forma que `PremiumShop`/`ItemCard` leem. */
export function itemPremium(item) {
  return {
    id: item.id,
    name: item.nome,
    type: item.categoria,
    price: item.preco,
    description: item.descricao || '',
    tooltipText: item.tooltip || '',
    quantity: item.quantidade,
    effectType: item.tipoDeEfeito,
    effectNumber: item.numeroDoEfeito,
    uniqueBuy: item.compraUnica,
    image: getItemImage(item.nome),
    /**
     * A tabela do baú, com a chance já em porcentagem — vem do servidor, da
     * mesma lista que o sorteio usa. Vazia para o que não é baú.
     */
    rewards: (item.recompensas || []).map((r) => ({
      type: r.tipo,
      name: r.nome,
      amount: r.quantidade,
      chance: r.chance,
    })),
  };
}

/**
 * Uma competição. O `status` que as telas leem não existe no V2 — é derivado
 * das datas aqui, com `remainingTime` em **milissegundos** (o `formatTime` da
 * tela divide por mil).
 */
export function competicao(c) {
  const agora = Date.now();
  const inicio = new Date(c.comecaEm).getTime();
  const fim = new Date(c.terminaEm).getTime();
  const hasStarted = agora >= inicio;
  const hasEnded = agora >= fim;
  return {
    id: c.id,
    name: c.nome,
    type: c.tipo,
    rewardType: c.tipoDeRecompensa,
    rewardValue: c.valorDaRecompensa,
    tier: c.tier,
    initDate: c.comecaEm,
    endDate: c.terminaEm,
    status: {
      hasStarted,
      hasEnded,
      remainingTime: hasEnded ? 0 : Math.max(0, (hasStarted ? fim : inicio) - agora),
    },
  };
}

/**
 * Uma posição do ranking de competição, no aninhamento que o template herdou
 * do populate do Strapi (`rank.player.user.username`). O nível do jogador não
 * vem na rota — a tela mostra o fallback.
 */
export function posicaoDaCompeticao(posicao) {
  return {
    id: posicao.posicao,
    number: posicao.pontuacao,
    player: { level: '?', user: { username: posicao.jogador } },
  };
}

/**
 * O clã por dentro, na forma de `ClanDetails.vue`: `clan_boost` com as chaves
 * `lvlMoreGold`/`moreGold` que o template lê, e `boosts[tipo].timeRemaining`
 * em ms. `waiting_members` não existe no V2 — entrar é imediato ou por
 * convite — e sai vazio para o bloco de aprovação nunca renderizar.
 */
export function claDetalhado(cla) {
  const clanBoost = {};
  const boosts = {};
  const custos = {};
  for (const boost of cla.boosts || []) {
    const sufixo = boost.tipo.charAt(0).toUpperCase() + boost.tipo.slice(1);
    clanBoost[boost.tipo] = boost.ativo;
    clanBoost[`lvl${sufixo}`] = boost.nivel;
    boosts[boost.tipo] = boost.ativo
      ? { timeRemaining: Math.max(0, new Date(boost.expiraEm).getTime() - Date.now()) }
      : null;
    custos[boost.tipo] = {
      upgrade: boost.custoDeUpgrade,
      activation: boost.custoDeAtivacao,
    };
  }
  return {
    id: cla.id,
    name: cla.nome,
    description: cla.descricao || '',
    type: cla.tipo,
    balance: cla.saldo,
    pontuation: cla.pontuacao,
    leader: cla.lider
      ? { id: cla.lider.id, username: cla.lider.username, level: cla.lider.nivel }
      : null,
    members: (cla.membros || []).map((m) => ({
      id: m.id,
      username: m.username,
      level: m.nivel,
    })),
    waiting_members: [],
    clan_boost: clanBoost,
    boosts,
    boostCosts: custos,

    /*
     * O nível do clã (1.1) — a espinha que o problema 2 do D22 dizia faltar:
     * "um clã de seis meses e um de ontem parecem a mesma coisa na tela".
     *
     * `xpParaOProximo` vem junto porque nível sem quanto falta é o problema 3
     * repetido — número solto no lugar de progresso. E `vagas`/`tetoDeBoost`
     * são o que o nível LIBERA: sem eles ele seria decorativo.
     */
    level: cla.nivel ?? 1,
    xp: cla.xp ?? 0,
    xpToNext: cla.xpParaOProximo ?? 0,
    slots: cla.vagas ?? 0,
    boostCap: cla.tetoDeBoost ?? 0,

    /*
     * A posição no ranking vem do SERVIDOR e não é mais pescada dentro do
     * ranking público: aquele tem 50 linhas, e o clã de posição 51 ficava sem
     * posição nenhuma — a metade do problema 6 do D22 que ninguém tinha visto.
     */
    rankingPosition: cla.posicaoNoRanking ?? null,
    flag: cla.bandeira ?? null,
    country: cla.pais ?? null,
  };
}

/** Um clã da lista pública — os clãs abertos da porta do clã (`ClanPage`). */
export function claDaLista(cla) {
  return {
    id: cla.id,
    name: cla.nome,
    description: cla.descricao || '',
    type: cla.tipo,
    memberCount: cla.membros,
    pontuation: cla.pontuacao,
    country: cla.pais ?? null,
    leader: { username: '' },
  };
}

/** Uma mensagem do chat, no aninhamento que `ClanChat.vue` lê. */
export function mensagemDoCla(m) {
  return {
    id: m.id,
    text: m.texto,
    createdAt: m.enviadaEm,
    player: { id: m.autorId, username: m.autor, user: { username: m.autor || '?' } },
  };
}

/** Um funcionário do agregado do negócio, na forma de `Business.vue`. */
export function funcionarioDoNegocio(f) {
  return {
    id: f.id,
    name: f.nome,
    rank: f.rank,
    level: f.nivel,
    fishingPower: Number(f.fishingPower),
    maxStamina: f.maxStamina,
    catchRate: Number(f.catchRate),
    criticalRate: Number(f.criticalRate),
    salary: f.salario,
    fishCaught: f.peixesCapturados,
    photo: f.foto,
    hasAlreadyFished: f.jaPescouHoje,
    map: f.mapaNome ? { id: f.mapaId, name: f.mapaNome } : null,
    equipment: {
      rod: f.vara ? { id: f.vara.id, name: f.vara.nome, image: getItemImage(f.vara.nome) } : null,
      bait: f.isca
        ? { id: f.isca.id, name: f.isca.nome, quantity: f.isca.quantidade }
        : null,
    },
  };
}

/**
 * Um candidato do sorteio. `id` é **string temporária** (vale 10 minutos, morre
 * na contratação); os campos que o V2 não sorteia saem com o neutro que o card
 * exibe sem quebrar.
 */
export function candidato(c) {
  return {
    id: c.id,
    name: c.nome,
    rank: c.rank,
    level: c.nivel,
    fishingPower: c.fishingPower,
    salary: c.salario,
    // As luvas: o ouro que sai UMA vez, na assinatura — o salário é o que sai
    // todo dia. O servidor manda o número junto do candidato, e é ele que a
    // ficha mostra e o botão cobra.
    hireCost: c.luvas,
    maxStamina: 100,
    catchRate: 0.5,
    criticalRate: 0.05,
    photo: 0,
  };
}

/** Um evento ativo, com `days` derivado do fim (o catálogo não viaja junto). */
export function eventoAtivo(e) {
  return {
    id: e.id,
    name: e.nome,
    description: e.descricao || '',
    effectType: e.tipoDeEfeito,
    effectNumber: e.numeroDoEfeito,
    typeEvent: e.tipo,
    endDate: e.terminaEm,
    days: e.dias ?? Math.max(1, Math.ceil((new Date(e.terminaEm) - Date.now()) / 86400000)),
  };
}

/** O mercado do dia, na forma que `market.js` guarda. */
export function mercadoDoDia(m) {
  return {
    lastUpdate: new Date(m.horaDoServidor).toDateString(),
    serverTime: m.horaDoServidor,
    favoriteFish: m.peixeFavorito ? { id: m.peixeFavorito.id, name: m.peixeFavorito.nome } : null,
    leastFavoriteFish: m.peixeMenosFavorito
      ? { id: m.peixeMenosFavorito.id, name: m.peixeMenosFavorito.nome }
      : null,
    favoriteMap: m.mapaFavorito,
    leastFavoriteMap: m.mapaMenosFavorito,
    dailyPriceMultipliers: m.multiplicadores || {},
  };
}

/** Um prêmio da roleta. A tela só usa `name`, mas tipo e valor vêm junto. */
export function premioDaRoleta(p) {
  return { id: p.id, name: p.nome, type: p.tipoDeRecompensa, value: p.valor };
}

/** O perfil público, na forma que `Profile.vue` renderiza sem guardas. */
export function perfilPublico(p) {
  return {
    username: p.username,
    level: p.nivel,
    experience: p.experiencia,
    maxExperience: p.maxExperiencia,
    fishCaught: p.totalDePeixes,
    gold: p.gold,
    maxStamina: p.staminaMaxima,
    joinedAt: p.criadoEm,
    fishingPower: p.atributos.fishingPower,
    catchRate: p.atributos.catchRate,
    criticalRate: p.atributos.criticalRate,
    reelSpeed: p.atributos.reelSpeed,
    equipment: {
      rod: p.equipamento.vara
        ? {
            name: p.equipamento.vara.nome,
            rarity: p.equipamento.vara.raridade,
            power: p.equipamento.vara.poder,
            durability: {
              current: p.equipamento.vara.durabilidade ?? 0,
              maxDurability: p.equipamento.vara.durabilidadeMaxima ?? 0,
            },
          }
        : null,
      boat: p.equipamento.barco
        ? {
            name: p.equipamento.barco.nome,
            rarity: p.equipamento.barco.raridade,
            level: p.equipamento.barco.nivel,
            speed: p.equipamento.barco.velocidade,
          }
        : null,
      // A tela trata a casa como **nome**, não objeto — herança do Strapi.
      house: p.equipamento.casa ? p.equipamento.casa.nome : null,
    },
    purchasedMaps: p.mapas,
    clan: p.cla ? { name: p.cla.nome, tag: '', level: 1 } : null,
    achievements: {
      total: p.conquistas.total,
      items: (p.conquistas.itens || []).map((c) => ({
        name: c.nome,
        description: c.descricao || '',
        condition: c.condicao,
        textCondition: null,
      })),
    },
  };
}
